<script setup>
import { nextTick, unref } from 'vue';
import UiFormField from './UiFormField.vue';
import { required as requiredRule } from '@/utils/validation';

// Renders form controls from a schema, bound with v-model:values to the
// page's form object (a ref; each edit replaces it). Field options:
//   key, label, type ('text' | 'textarea' | 'number' | 'date' | 'time' | 'password'
//   | 'select' | 'autocomplete' | 'file'), required, rules, placeholder,
//   description, items (array or ref) / itemTitle / itemValue (selects), multiple,
//   accept (file), min / max (date, number), span (2 = full width in a
//   grid), hidden (bool or (values) => bool), disabled, autocomplete (the
//   input's autocomplete hint, e.g. 'given-name'; `name` is always the key).
defineProps({
  fields: { type: Array, required: true },
});
const values = defineModel('values', { type: Object, required: true });

// Browser autofill and form-filler tools set several fields in the same
// tick, before the parent's new object comes back down as the prop. Build
// on the last object sent this tick, or each field would copy the stale one
// and wipe the fields filled just before it.
let pending = null;
function set(key, value) {
  if (!pending) nextTick(() => (pending = null));
  pending = { ...(pending ?? values.value), [key]: value };
  values.value = pending;
}

const isHidden = (field) =>
  typeof field.hidden === 'function' ? field.hidden(values.value) : !!field.hidden;

const rulesFor = (field) => [...(field.required ? [requiredRule] : []), ...(field.rules || [])];

const INPUT_TYPES = { number: 'number', date: 'date', time: 'time', password: 'password' };
</script>

<template>
  <template v-for="field in fields" :key="field.key">
    <UiFormField
      v-if="!isHidden(field)"
      :label="field.label"
      :description="field.description"
      :required="field.required"
      :class="{ 'span-2': field.span === 2 }"
      v-slot="{ id }"
    >
      <v-textarea
        v-if="field.type === 'textarea'"
        :id="id"
        :name="field.key"
        :autocomplete="field.autocomplete"
        :model-value="values[field.key]"
        @update:model-value="set(field.key, $event)"
        :placeholder="field.placeholder"
        :rules="rulesFor(field)"
        :disabled="field.disabled"
        rows="3"
        auto-grow
        hide-details="auto"
      />
      <component
        :is="field.type === 'autocomplete' ? 'v-autocomplete' : 'v-select'"
        v-else-if="field.type === 'select' || field.type === 'autocomplete'"
        :id="id"
        :name="field.key"
        :autocomplete="field.autocomplete"
        :model-value="values[field.key]"
        @update:model-value="set(field.key, $event)"
        :items="unref(field.items) || []"
        :item-title="field.itemTitle || 'title'"
        :item-value="field.itemValue || 'value'"
        :multiple="field.multiple"
        :chips="field.multiple"
        :placeholder="field.placeholder || 'Select…'"
        :rules="rulesFor(field)"
        :disabled="field.disabled"
        hide-details="auto"
      />
      <v-file-input
        v-else-if="field.type === 'file'"
        :id="id"
        :name="field.key"
        :autocomplete="field.autocomplete"
        :model-value="values[field.key]"
        @update:model-value="set(field.key, $event)"
        :accept="field.accept"
        :placeholder="field.placeholder || 'Choose a file'"
        :rules="rulesFor(field)"
        :disabled="field.disabled"
        prepend-icon=""
        prepend-inner-icon="mdi-paperclip"
        hide-details="auto"
      />
      <v-text-field
        v-else
        :id="id"
        :name="field.key"
        :autocomplete="field.autocomplete"
        :model-value="values[field.key]"
        @update:model-value="set(field.key, $event)"
        :type="INPUT_TYPES[field.type] || 'text'"
        :min="field.min"
        :max="field.max"
        :placeholder="field.placeholder"
        :rules="rulesFor(field)"
        :disabled="field.disabled"
        hide-details="auto"
      />
    </UiFormField>
  </template>
</template>
